import { Fragment, useMemo } from "react";
import { cn } from "@/utils/cn";
import { Icon } from "@/components/Icon/Icon";
import type { Target } from "@/utils/types/flavor";
import type { ReplacementPreview } from "@/utils/formulation/computeCurrent";
import type {
  ReplacementSet,
  ReplacementSetItem,
} from "@/services/workbench_api";
import type { SubstanceSupplyFlags } from "@/utils/types/substance";
import { Tooltip } from "./Tooltip";
import {
  RowSupplyIcons,
  type SupplyModalKind,
} from "@/components/Modals/Supply/RowSupplyIcons";
import { dropDelta, swapDelta } from "@/utils/formulation/replacementLoss";
import { lookupServerCompound } from "@/utils/serverCompounds";

type ReplacementSubRowsProps = {
  compoundId: string;
  target: Target | null | undefined;
  activePreview: ReplacementPreview | null;
  onPreviewSelect: (payload: ReplacementPreview) => void;
  onClose: () => void;
  onOpenSupply?: (kind: SupplyModalKind, id: string) => void;
  baseLoss: number | null;      // NEW — replaces drops
  dropOnlyLoss: number | null;  // NEW — replaces drops
  singles?: ReplacementSet[];
  multis?: ReplacementSet[];
  /** Source row's batch-supply-flags entry, used to derive the header pills. */
  flags: SubstanceSupplyFlags | null;
  /** Map keyed by candidate compoundId ("compound:<id>" / "food:<id>") →
   *  the candidate's batch-supply-flags. Undefined value === still loading.
   *  Sourced from FlaskPanel's per-substance hook, which now batches
   *  candidate keys into the same fetch as the in-flask rows. */
  flagsByCandidate: Record<string, SubstanceSupplyFlags | undefined>;
};

function candidateKey(it: ReplacementSetItem): string {
  return `${it.itemNdbTable === "public.compound" ? "compound" : it.itemNdbTable === "public.food" ? "food" : it.itemNdbTable}:${it.itemRowid}`;
}

function idsMatch(a: string[] | undefined, b: string[]): boolean {
  if (!a) return false;
  if (a.length !== b.length) return false;
  return [...a].sort().join("|") === [...b].sort().join("|");
}

function nameFor(it: ReplacementSetItem): string {
  return it.name ?? `compound ${it.itemRowid}`;
}

function lossColorForDelta(delta: number, baseLoss: number | null): string {
  // Negative delta = improvement (green). Large positive = regression (red).
  // Threshold the "large regression" by relative magnitude so the color
  // cue is consistent across flavors with different base losses. The 4%
  // cutoff is the sqrt-scale equivalent of the prior 8% squared-scale
  // threshold: √1.08 ≈ 1.039, i.e. a ~4% increase in sqrt-loss matches an
  // 8% increase in squared-loss.
  if (delta < 0) return "var(--brand-green)";
  if (baseLoss && baseLoss > 0 && (delta / baseLoss) * 100 > 4) {
    return "#c92619";
  }
  return "#d97706";
}

function formatDelta(delta: number): string {
  const sign = delta >= 0 ? "+" : "";
  return `${sign}${delta.toFixed(3)}`;
}

export function ReplacementSubRows({
  compoundId,
  target,
  activePreview,
  onPreviewSelect,
  onClose,
  onOpenSupply,
  baseLoss,
  dropOnlyLoss,
  singles,
  multis,
  flags,
  flagsByCandidate,
}: ReplacementSubRowsProps) {
  void target; // reserved for future per-row gating; not used in this rewrite

  const sourceName = lookupServerCompound(compoundId)?.name ?? compoundId;
  const isLoading = singles === undefined && multis === undefined;

  // Synthesize a Drop row from the per-item base + dropOnly. Always rendered
  // at the top of the candidate list, even when dropping would worsen the
  // loss (positive delta) — dropping is a valid action regardless.
  const dropD = dropDelta(baseLoss, dropOnlyLoss);
  const dropRowAvailable = dropD != null;

  // Rank singles + multis: viable bucket first (has supplier AND non-Danger),
  // non-viable bucket fills the remainder. Within each bucket, sort by delta
  // ascending (most negative = biggest improvement on top). Cap at 5.
  type Scored = { set: ReplacementSet; delta: number };
  const swaps = useMemo<Scored[]>(() => {
    const all = [...(singles ?? []), ...(multis ?? [])];
    const scored: Scored[] = [];
    for (const set of all) {
      const d = swapDelta(baseLoss, set.replacedLoss);
      if (d == null) continue;
      scored.push({ set, delta: d });
    }
    const isViable = (s: ReplacementSet): boolean =>
      s.hasSupplier === true && s.safetySignal !== "Danger";
    scored.sort((a, b) => {
      const va = isViable(a.set) ? 0 : 1;
      const vb = isViable(b.set) ? 0 : 1;
      if (va !== vb) return va - vb;       // viable first
      return a.delta - b.delta;             // ascending delta within bucket
    });
    return scored.slice(0, 5);
  }, [singles, multis, baseLoss]);

  // Header pills — derived from the source row's real supply data +
  // candidate viability.
  const pills: Array<{ text: string; severity: "info" | "warning" | "danger" }> = [];
  if (flags) {
    const noSupplier =
      flags.marketCheapest == null &&
      swaps.some((s) => s.set.hasSupplier === true);
    if (noSupplier) {
      pills.push({ text: "No suppliers registered", severity: "warning" });
    }
    const danger =
      flags.safetySummary?.signal === "Danger" &&
      swaps.some((s) => s.set.safetySignal !== "Danger");
    if (danger) {
      pills.push({ text: "Danger hazard", severity: "danger" });
    }
  }
  const anyImproving =
    (dropD != null && dropD < 0) ||
    swaps.some((s) => s.delta < 0);
  if (anyImproving) {
    pills.push({ text: "Closer match available", severity: "info" });
  }

  const candidateCount = (dropRowAvailable ? 1 : 0) + swaps.length;

  return (
    <div className="replacement-tray">
      <div
        className="replacement-tray-header"
        style={{
          flexDirection: "column",
          alignItems: "stretch",
          gap: 4,
        }}
      >
        <span className="replacement-tray-rail-v" />
        <span className="replacement-tray-rail-h" />
        <div
          className="hstack"
          style={{
            alignItems: "center",
            gap: 8,
            minWidth: 0,
          }}
        >
          <Icon name="wand" size={12} />
          <span
            className="label"
            style={{
              margin: 0,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              minWidth: 0,
              flex: "0 1 auto",
            }}
          >
            Replacement options for {sourceName}
          </span>
          <span
            className="mono"
            style={{
              fontSize: 10,
              color: "var(--ink-4)",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {isLoading
              ? "computing…"
              : `${candidateCount} candidate${candidateCount === 1 ? "" : "s"} · select to preview`}
          </span>
          <span style={{ flex: 1 }} />
          <button
            type="button"
            onClick={onClose}
            className="btn xs ghost"
            style={{ padding: "3px 6px", flexShrink: 0 }}
          >
            <Icon name="x" size={11} />
          </button>
        </div>
        {pills.length > 0 && (
          <div
            className="hstack"
            style={{
              gap: 4,
              alignItems: "center",
              paddingLeft: 20,
            }}
          >
            {pills.map((pill) => (
              <span
                key={pill.text}
                className={cn("replacement-reason-pill", pill.severity)}
              >
                {pill.text}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="replacement-col-head">
        <span style={{ flex: 1 }}>Candidate</span>
        <Tooltip
          label="Change in loss² vs current flask"
          sublabel="Negative → improvement · Positive → regression"
        >
          <span
            className="hstack"
            style={{
              width: 140,
              justifyContent: "flex-end",
              gap: 5,
              alignItems: "center",
              cursor: "help",
            }}
          >
            <span>Δ loss vs base</span>
          </span>
        </Tooltip>
        <span style={{ width: 16, flexShrink: 0 }} />
        <span style={{ width: 96, textAlign: "right" }}>Info</span>
        <span style={{ width: 16, flexShrink: 0 }} />
        <span style={{ width: 56 }} />
      </div>

      {isLoading && (
        <div className="replacement-row" style={{ opacity: 0.5 }}>
          <span style={{ flex: 1, fontSize: 12, color: "var(--ink-4)" }}>
            Computing replacements…
          </span>
        </div>
      )}

      {/* Drop row — synthesized from per-item baseLoss + dropOnlyLoss.
          Always rendered at the top when delta is available, even when
          dropping worsens the loss. */}
      {!isLoading && dropRowAvailable && (() => {
        const isSelected =
          activePreview?.fromId === compoundId && activePreview?.isDrop === true;
        const lossColor = lossColorForDelta(dropD, baseLoss);
        const payload: ReplacementPreview = {
          fromId: compoundId,
          toIds: [],
          deltaLoss: dropD,
          sourceName,
          replName: undefined,
          isDrop: true,
        };
        return (
          <div
            key="drop"
            onClick={() => onPreviewSelect(payload)}
            className={cn("replacement-row", isSelected && "selected")}
          >
            <span className="row-rail-v" />
            <span className="row-rail-h" />
            {isSelected && <span className="selected-stripe" />}

            <div
              style={{
                width: 4,
                height: 22,
                borderRadius: 2,
                background: "var(--line-strong)",
                opacity: 0.6,
                flexShrink: 0,
              }}
            />

            <div
              className="vstack"
              style={{ flex: 1, minWidth: 0, lineHeight: 1.2 }}
            >
              <div
                className="hstack gap-2"
                style={{ alignItems: "center", minWidth: 0 }}
              >
                <span
                  style={{
                    fontSize: 12.5,
                    fontWeight: 500,
                    color: "var(--ink-2)",
                    fontStyle: "italic",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {`Drop ${sourceName}`}
                </span>
                {isSelected && <span className="previewing-tag">PREVIEWING</span>}
              </div>
              <span
                className="mono"
                style={{
                  fontSize: 10,
                  color: "var(--ink-4)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Remove from flask without replacement
              </span>
            </div>

            <div className="delta-loss-cell" style={{ color: lossColor }}>
              {formatDelta(dropD)}
            </div>

            <span style={{ width: 16, flexShrink: 0 }} />
            <span style={{ width: 96 }} />
            <span style={{ width: 16, flexShrink: 0 }} />
            <span style={{ width: 56 }} />
          </div>
        );
      })()}

      {/* Swap rows — singles + multis filtered and ranked. */}
      {!isLoading &&
        swaps.map((scored, i) => {
          const { set, delta: dDelta } = scored;

          const ids = set.items.map(candidateKey);
          const primaryItem = set.items[0];
          const secondaryItem = set.items[1];

          const isSelected =
            !!activePreview &&
            !activePreview.isDrop &&
            activePreview.fromId === compoundId &&
            idsMatch(activePreview.toIds, ids);

          const lossColor = lossColorForDelta(dDelta, baseLoss);
          const candidateName = primaryItem ? nameFor(primaryItem) : "Unknown";
          const secondaryName = secondaryItem ? nameFor(secondaryItem) : undefined;

          const payload: ReplacementPreview = {
            fromId: compoundId,
            toIds: ids,
            deltaLoss: dDelta,
            sourceName,
            replName: secondaryName
              ? `${candidateName} + ${secondaryName}`
              : candidateName,
            isDrop: false,
          };

          const primaryKey = primaryItem ? candidateKey(primaryItem) : null;
          const primaryFlags = primaryKey ? flagsByCandidate[primaryKey] : undefined;

          return (
            <div
              key={`swap-${i}`}
              onClick={() => onPreviewSelect(payload)}
              className={cn("replacement-row", isSelected && "selected")}
            >
              <span className="row-rail-v" />
              <span className="row-rail-h" />
              {isSelected && <span className="selected-stripe" />}

              <div
                style={{
                  width: 4,
                  height: 22,
                  borderRadius: 2,
                  background: "var(--fam-default)",
                  opacity: 1,
                  flexShrink: 0,
                }}
              />

              <div
                className="vstack"
                style={{ flex: 1, minWidth: 0, lineHeight: 1.2 }}
              >
                <div
                  className="hstack gap-2"
                  style={{ alignItems: "center", minWidth: 0 }}
                >
                  <span
                    style={{
                      fontSize: 12.5,
                      fontWeight: 500,
                      color: "var(--ink)",
                      fontStyle: "normal",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {candidateName}
                  </span>
                  {secondaryName && (
                    <Fragment>
                      <span style={{ color: "var(--ink-4)", fontSize: 11 }}>+</span>
                      <span
                        style={{
                          fontSize: 12.5,
                          fontWeight: 500,
                          color: "var(--ink)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {secondaryName}
                      </span>
                    </Fragment>
                  )}
                  {isSelected && <span className="previewing-tag">PREVIEWING</span>}
                </div>
                <span
                  className="mono"
                  style={{
                    fontSize: 10,
                    color: "var(--ink-4)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {primaryItem ? primaryItem.cas ?? "" : ""}
                </span>
              </div>

              <div className="delta-loss-cell" style={{ color: lossColor }}>
                {formatDelta(dDelta)}
              </div>

              <span style={{ width: 16, flexShrink: 0 }} />
              {primaryKey && onOpenSupply ? (
                <RowSupplyIcons
                  compoundId={primaryKey}
                  flags={primaryFlags ?? null}
                  loading={primaryFlags === undefined}
                  onOpen={onOpenSupply}
                />
              ) : (
                <span style={{ width: 96 }} />
              )}

              <span style={{ width: 16, flexShrink: 0 }} />
              <span style={{ width: 56 }} />
            </div>
          );
        })}

      {!isLoading && !dropRowAvailable && swaps.length === 0 && (
        <div className="replacement-row" style={{ opacity: 0.6 }}>
          <span style={{ flex: 1, fontSize: 12, color: "var(--ink-4)" }}>
            No replacement options found.
          </span>
        </div>
      )}
    </div>
  );
}
